<template>
  <div class="card overflow-hidden">
    <div class="overflow-x-auto">
      <table class="w-full">
        <thead>
          <tr class="tt-head">
            <th class="text-left">Status</th>
            <th class="text-left">Title</th>
            <th class="text-left whitespace-nowrap">Organisation</th>
            <th class="text-left whitespace-nowrap">Tender ID</th>
            <th class="text-left whitespace-nowrap">Portal</th>
            <th v-if="showProjectStatus" class="text-left whitespace-nowrap">Bid</th>
            <th class="text-left whitespace-nowrap">Closing</th>
            <th class="text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="tender in tenders"
            :key="tender.id"
            @click="$router.push(`/tender-search/${tender.id}`)"
            class="border-b border-slate-100 last:border-b-0 hover:bg-primary-50/40 transition-colors duration-200 cursor-pointer group"
          >
            <!-- Status. The deadline badge answers "can I still submit?"; the
                 type below it answers "is this still a tender at all?" — an
                 awarded contract carries no closing date, so the deadline badge
                 alone would leave it looking open. Only shown when the portal
                 publishes one, and not when it merely repeats the deadline. -->
            <td class="px-5 py-4">
              <div class="flex flex-col items-start gap-1">
                <StatusBadge :type="statusOf(tender).type" :label="statusOf(tender).label" />
                <span v-if="showsType(tender)" class="type-tag">
                  {{ listingTypeLabel(tender.listing_type) }}
                </span>
              </div>
            </td>

            <!-- Title -->
            <!-- The link makes each row reachable by keyboard and screen
                 reader; the row click stays as the larger mouse target. -->
            <td class="px-5 py-4 max-w-md">
              <router-link
                :to="`/tender-search/${tender.id}`"
                @click.stop
                class="font-semibold text-slate-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-600 focus-visible:text-primary-600 transition-colors"
              >
                <HighlightText :text="tender.title || 'Untitled'" :query="highlight" />
              </router-link>
            </td>

            <!-- Org -->
            <td class="px-5 py-4 max-w-[200px]">
              <p class="text-sm text-slate-500 line-clamp-1">
                <HighlightText :text="tender.organisation_name || '—'" :query="highlight" />
              </p>
            </td>

            <!-- Tender ID first: the full reference ended with it and was
                 clipped, so the ID people search with never showed. -->
            <td class="px-5 py-4 max-w-[200px]" :title="tender.tender_reference || ''">
              <p class="text-xs font-mono text-slate-700 break-all">
                <HighlightText :text="tenderIdOf(tender.tender_reference) || '—'" :query="highlight" />
              </p>
              <p v-if="refNoOf(tender.tender_reference)" class="text-xs text-slate-500 line-clamp-1 mt-0.5">
                Ref <HighlightText :text="refNoOf(tender.tender_reference)" :query="highlight" />
              </p>
            </td>

            <!-- Portal -->
            <td class="px-5 py-4 whitespace-nowrap">
              <span class="text-xs font-semibold uppercase text-slate-600 bg-slate-100 px-2.5 py-1 rounded-md">
                {{ tender.portal_code }}
              </span>
            </td>

            <!-- Project Status -->
            <td v-if="showProjectStatus" class="px-5 py-4 whitespace-nowrap">
              <!-- Whether a bid is being prepared from this bookmark — not the
                   tender's own status (that is the Status column). The text
                   carries the state; the tint only reinforces it. -->
              <span
                class="text-xs font-semibold px-2 py-1 rounded"
                :class="tender.project_status === 'active'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-slate-100 text-slate-600'"
              >
                {{ tender.project_status === 'active' ? 'Preparing' : 'Bookmarked' }}
              </span>
            </td>

            <!-- Closing -->
            <td class="px-5 py-4 whitespace-nowrap">
              <p class="text-sm text-slate-800 font-semibold">
                {{ formatDate(tender.closing_date) }}
              </p>
              <!-- Deadline countdown: amber with a clock inside the 72-hour
                   window, quiet grey otherwise. -->
              <p
                v-if="countdownOf(tender)"
                class="text-xs mt-0.5 flex items-center gap-1"
                :class="countdownOf(tender).urgent ? 'text-amber-700 font-semibold' : 'text-slate-500'"
              >
                <ClockIcon v-if="countdownOf(tender).urgent" class="w-3.5 h-3.5" aria-hidden="true" />
                {{ countdownOf(tender).text }}
              </p>
            </td>

            <!-- Actions -->
            <td class="px-5 py-4 text-right whitespace-nowrap">
              <div class="flex items-center justify-end gap-3">
                <!-- Go: marks the bookmark as being prepared (it shows
                     under Bids › To Upload) and opens the upload page for it.
                     Viewers can't upload, so they don't get the button. -->
                <button
                  v-if="showProjectStatus && !viewOnly"
                  type="button"
                  @click.stop="onPrepareBid(tender)"
                  :aria-label="`Go: upload PDFs for ${tender.title || 'this tender'}`"
                  class="bg-primary-50 text-[#0071C5] hover:bg-[#0071C5] hover:text-white border border-primary-200 hover:border-[#0071C5] px-4 py-1.5 rounded-full text-xs font-bold transition-colors shadow-sm whitespace-nowrap"
                >
                  Go
                </button>
                <button
                  @click.stop="onBookmark(tender)"
                  :aria-label="isBookmarked(tender.id) ? 'Remove bookmark' : 'Bookmark'"
                  :aria-pressed="isBookmarked(tender.id)"
                  class="p-1.5 rounded-md hover:bg-amber-100 transition-colors duration-200"
                >
                  <StarSolidIcon v-if="isBookmarked(tender.id)" class="w-4 h-4 text-amber-500" aria-hidden="true" />
                  <StarIcon v-else class="w-4 h-4 text-slate-500" aria-hidden="true" />
                </button>
                <ArrowRightIcon class="w-4 h-4 text-slate-500 group-hover:text-primary-600 transition-colors" aria-hidden="true" />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <LoginPromptModal
      :show="showLoginPrompt"
      message="Please login to bookmark tenders."
      @close="showLoginPrompt = false"
    />
  </div>
</template>

<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import {
  StarIcon,
  ArrowRightIcon,
  ClockIcon,
} from "@heroicons/vue/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/vue/24/solid";

import StatusBadge from "./StatusBadge.vue";
import { listingTypeLabel, isDecided } from "../utils/listingType";
import HighlightText from "./HighlightText.vue";
import LoginPromptModal from "./LoginPromptModal.vue";
import { useBookmarks } from "../store/bookmarks";
import { formatDate, deadlineCountdown, getTenderStatus } from "../utils/dateUtils";
import { tenderIdOf, refNoOf } from "../utils/tenderId";
import { isLoggedIn, isViewOnly } from "../utils/auth";

defineProps({
  tenders: { type: Array, required: true },
  showProjectStatus: { type: Boolean, default: false },
  // The applied search term; matches are marked in title, organisation and ID.
  highlight: { type: String, default: "" },
});

const { isBookmarked, toggle, updateStatus } = useBookmarks();
const toast = useToast();
const router = useRouter();
const showLoginPrompt = ref(false);
const viewOnly = isViewOnly();

const statusOf    = (t) => getTenderStatus(t.closing_date);

// Only the types that say something the deadline badge does not. "Live" and
// "Active" repeat it, so showing them would be noise on every row.
const showsType = (t) => !!t.listing_type && isDecided(t.listing_type);
const countdownOf = (t) => deadlineCountdown(t.closing_date);

async function onBookmark(tender) {
  if (!isLoggedIn()) {
    showLoginPrompt.value = true;
    return;
  }
  const wasBookmarked = isBookmarked(tender.id);
  try {
    await toggle(tender);
    toast.success(wasBookmarked ? "Removed from bookmarks" : "Bookmarked");
  } catch {
    toast.error("Couldn't update the bookmark. Check your connection and try again.");
  }
}

// Marks the bookmark as being prepared, then opens the upload page with the
// tender attached so the new bid is linked to it.
async function onPrepareBid(tender) {
  if (!isLoggedIn()) {
    showLoginPrompt.value = true;
    return;
  }
  const tenderId = tender.tender_id ?? tender.id;
  if (tender.project_status !== "active") {
    try {
      await updateStatus(tenderId, "active");
    } catch {
      toast.error("Couldn't start preparing this bid. Check your connection and try again.");
      return;
    }
  }
  router.push({
    name: "Home",
    query: { tender_id: tenderId, tender_name: tender.title || "" },
  });
}
</script>

<style scoped>
/* The tender's list on its portal (Awarded, Under Evaluation, In Progress).
   Deliberately quieter than StatusBadge: the deadline is the primary signal
   and this qualifies it, so it reads as a caption rather than a second badge
   competing with the first. #475569 on #f1f5f9 is 7.4:1. */
.type-tag {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.2;
    color: #475569;
    background: #f1f5f9;
    border-radius: 4px;
    padding: 0.15rem 0.4rem;
    white-space: nowrap;
}

/* Header styling matches the app's other tables (Bids, Bid Summary,
   Standard Documents): one tint, one weight, one tracking. */
.tt-head {
    background: #f1f5f9;
    border-bottom: 2px solid #e2e8f0;
}

.tt-head th {
    vertical-align: middle;
    padding: 0.75rem 1.25rem;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #475569;
}
</style>
